import { normalizeChatMessageResponse } from './chatMappers'
import { normalizeFeedbackState } from './messageFeedback'
import { structureChatMessage } from './chatTextStructurer'
import { ChatBubble, ChatHistoryItem, ChatHistoryList } from './types'

/** Sunucu ayarı gelmezse kullanılacak güvenli eşik (backend varsayılanıyla aynı). */
const DEFAULT_RESUME_WINDOW_MINUTES = 120

/** Backend PascalCase / camelCase ayrımını tolere eden alan okuyucu. */
function pick(source: unknown, ...keys: string[]): unknown {
  if (!source || typeof source !== 'object') return undefined
  const record = source as Record<string, unknown>
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) return record[key]
  }
  return undefined
}

function toText(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * ISO tarihi epoch ms'e çevirir; ayrıştırılamazsa 0 (etiket çizilmez — `MessageBubble`'ın
 * falsy kontrolü buna dayanır, bu yüzden dönüş sözleşmesi `undefined` yapılmaz).
 *
 * KRİTİK (final whole-branch review, Bulgu 1): axios katmanı `src/base/proxy/serviceProxy.ts`
 * her yanıtı `customJsonDateParser` ile `JSON.parse` eder; bu revive edici, ondalık saniyeli ama
 * "Z" son eki OLMAYAN tarih dizgilerini (backend Kind=Unspecified DATETIME2 döndürürse ortaya
 * çıkan tam biçim) bile bir `Date` nesnesine çevirir — mapper bu alanı hiç string olarak görmez.
 * Eski gerçekleme `typeof value !== 'string'` ile bunu 0'a düşürüyordu (auto-resume hiç
 * tetiklenmiyordu). Burada hem `Date` örneğini hem sayıyı da kabul ediyoruz.
 */
function toEpochMs(value: unknown): number {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? 0 : value.getTime()
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value !== 'string') return 0
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

export function normalizeHistoryList(value: unknown): ChatHistoryList {
  const rawWindow = pick(value, 'resumeWindowMinutes', 'ResumeWindowMinutes')
  const rawConversations = pick(value, 'conversations', 'Conversations')

  const conversations: ChatHistoryItem[] = Array.isArray(rawConversations)
    ? rawConversations
        .map(row => ({
          conversationId: Number(pick(row, 'conversationId', 'ConversationId') ?? 0),
          firstUserText: toText(pick(row, 'firstUserText', 'FirstUserText')),
          lastActivityUtc: toEpochMs(pick(row, 'lastActivityUtc', 'LastActivityUtc')),
        }))
        .filter(item => item.conversationId > 0)
    : []

  return {
    resumeWindowMinutes:
      typeof rawWindow === 'number' && rawWindow > 0 ? rawWindow : DEFAULT_RESUME_WINDOW_MINUTES,
    conversations,
  }
}

/**
 * Geçmiş mesajları balonlara çevirir. Payload varsa CANLI yanıtla aynı boru hattından
 * (normalizeChatMessageResponse → structureChatMessage) geçer; yoksa düz metin balonu kurulur.
 *
 * KRİTİK (ADR-014): `form` BİLEREK taşınmaz. Geçmiş bir balon /chat/message'a istek atamaz —
 * iki hafta önceki bir "Onaylıyorum" butonu yan etkili işlemi tekrar tetiklemesin.
 */
export function historyMessagesToBubbles(value: unknown, nextId: () => number): ChatBubble[] {
  if (!Array.isArray(value)) return []

  return value.map(row => {
    const role = toText(pick(row, 'role', 'Role')) === 'user' ? 'user' : 'bot'
    const text = toText(pick(row, 'text', 'Text'))
    const createdAt = toEpochMs(pick(row, 'createdAtUtc', 'CreatedAtUtc'))
    const payload = pick(row, 'payload', 'Payload')
    // ADR-015: geri bildirim çapası ve varsa bu kullanıcının kendi oyu.
    const messageId = Number(pick(row, 'messageId', 'MessageId') ?? 0)
    const rawFeedback = pick(row, 'feedback', 'Feedback')
    const feedback = rawFeedback ? normalizeFeedbackState(rawFeedback) : undefined

    if (role === 'user' || payload === undefined) {
      return { id: nextId(), role, text, createdAt, messageId, feedback }
    }

    // Satırdaki Text yetkilidir; payload yalnız kartları taşır.
    const response = { ...normalizeChatMessageResponse(payload), text }
    const { text: structuredText, extras } = structureChatMessage(response)
    const { form: _droppedForm, ...replayableExtras } = extras

    return { id: nextId(), role, text: structuredText, createdAt, messageId, feedback, ...replayableExtras }
  })
}

/**
 * Widget açılışında son sohbet kaldığı yerden mi açılmalı? Eşik sunucudan gelir.
 * Eşiği aşan (bayat) sohbet otomatik açılmaz: karşılama balonu index 0'da kalır ve
 * onun altındaki hızlı-başlama kartları (ActionCards) görünür olmaya devam eder.
 */
export function shouldResume(
  item: ChatHistoryItem | undefined,
  resumeWindowMinutes: number,
  now: number
): boolean {
  if (!item || item.lastActivityUtc <= 0) return false
  return now - item.lastActivityUtc <= resumeWindowMinutes * 60 * 1000
}

/** Geçmiş listesi bölüm başlığı. 14 günlük pencerede "Bugün" tek başlık olarak yetmez. */
export function historyGroupLabel(epochMs: number, now: number): string {
  const startOfDay = (value: number) => {
    const date = new Date(value)
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  }
  const dayDiff = Math.round((startOfDay(now) - startOfDay(epochMs)) / (24 * 60 * 60 * 1000))
  if (dayDiff <= 0) return 'Bugün'
  if (dayDiff === 1) return 'Dün'
  return 'Daha Önce'
}

/**
 * Bir asenkron işlemin başlattığı üretici (generation) değeri hâlâ geçerli mi? `sendChatRequest`'teki
 * üretici kontrolünün ortak biçimi: kullanıcı bekleme sırasında araya girip yeni sohbet başlattı,
 * başka bir geçmiş kaydı seçti veya geçmişi sildiyse, eski/gecikmiş sonuç asla ekrana yansımamalıdır.
 */
export function isGenerationCurrent(capturedGeneration: number, currentGeneration: number): boolean {
  return capturedGeneration === currentGeneration
}

/**
 * Üretici korumalı asenkron uygulama. `fetchFn` çözülene kadar kullanıcı araya girip üretici
 * sayacını ilerletirse (`generationRef.current` değişirse) `applyFn` hiç çağrılmaz — geciken bir
 * sonuç asla o anki sohbetin üzerine yazmaz. ChatWidget'ta geçmiş seçimi (`handleSelectHistory`)
 * ve geçmişe otomatik devam efekti bu deseni kullanır (bkz. ADR-014 devam/silme yarış durumu düzeltmesi).
 * `fetchFn` reddederse hata çağırana yayılır; `applyFn` bu durumda da çağrılmaz.
 */
export async function applyIfCurrentGeneration<T>(
  capturedGeneration: number,
  generationRef: { current: number },
  fetchFn: () => Promise<T>,
  applyFn: (result: T) => void
): Promise<void> {
  const result = await fetchFn()
  if (isGenerationCurrent(capturedGeneration, generationRef.current)) applyFn(result)
}

/**
 * Widget açılışında geçmişe otomatik devam akışı. TABAN DURUMU GARANTİSİ: hangi dal çalışırsa
 * çalışsın (devam edilebilir sohbet yok / üretici bekleme sırasında geçersiz kılındı — ör.
 * kullanıcı "Yeni Sohbet"e bastı ya da HERHANGİ bir geçmiş kaydını sildi / ağ hatası) panel
 * balonsuz kalmaz: gerçek balonlar uygulanmadıysa `applyGreeting` en az bir kez çağrılır.
 *
 * `applyGreeting` KENDİSİ üretici bağımsızdır — kilit kural şudur: karşılamayı yalnızca mesaj
 * listesi HÂLÂ boşsa eklemek çağıranın sorumluluğundadır (ör. `setMessages(prev => prev.length ?
 * prev : [...])`). Böylece bu fonksiyon "bayat" kabul edip balon uygulamadığı bir durumda bile,
 * kullanıcı o sırada zaten "Yeni Sohbet"e basıp kendi karşılamasını almışsa ikinci bir karşılama
 * ASLA eklenmez — `applyGreeting` çağrısı orada no-op olur.
 */
export async function resumeOnOpen(
  generationRef: { current: number },
  api: {
    fetchHistoryList: () => Promise<unknown>
    fetchMessages: (conversationId: number) => Promise<unknown>
  },
  callbacks: {
    setHistory: (items: ChatHistoryItem[]) => void
    applyBubbles: (bubbles: ChatBubble[], conversationId: number) => void
    applyGreeting: () => void
  },
  nextId: () => number
): Promise<void> {
  // Bu efekt henüz hiçbir await'e girmeden yakalanır: aynı senkron an içinde üretici değişemez.
  const generation = generationRef.current

  let raw: unknown
  try {
    raw = await api.fetchHistoryList()
  } catch {
    callbacks.applyGreeting()
    return
  }

  const list = normalizeHistoryList(raw)
  callbacks.setHistory(list.conversations)

  const newest = list.conversations[0]
  if (!shouldResume(newest, list.resumeWindowMinutes, Date.now())) {
    callbacks.applyGreeting()
    return
  }

  let appliedBubbles = false
  try {
    await applyIfCurrentGeneration(
      generation,
      generationRef,
      () => api.fetchMessages(newest.conversationId),
      messages => {
        const bubbles = historyMessagesToBubbles(messages, nextId)
        if (bubbles.length > 0) {
          callbacks.applyBubbles(bubbles, newest.conversationId)
          appliedBubbles = true
        }
      }
    )
  } catch {
    // Mesaj getirme hatası: taban durumu aşağıdaki applyGreeting ile sağlanır.
  } finally {
    if (!appliedBubbles) callbacks.applyGreeting()
  }
}
